import * as React from "react"
import { type Contact } from "@digits-graphql/frontend/graphql-public"
import { ModuleLoading } from "@digits-shared/components/Loaders"
import useRouter from "@digits-shared/hooks/useRouter"
import { AnimatePresence } from "framer-motion"
import { CreatePasswordReset } from "src/frontend/components/Session/Glow/PasswordReset/CreatePasswordResetForm"
import { PASSWORD_RESET_RECIPIENT } from "src/frontend/components/Session/Glow/PasswordReset/locationState"
import { PasswordResetExpired } from "src/frontend/components/Session/Glow/PasswordReset/PasswordResetExpired"
import { ResetPasswordForm } from "src/frontend/components/Session/Glow/PasswordReset/ResetPasswordForm"
import { ResetPasswordSent } from "src/frontend/components/Session/Glow/PasswordReset/ResetPasswordSent"
import { useReadPasswordReset } from "src/frontend/components/Session/Glow/PasswordReset/useReadPasswordReset"
import { slide } from "src/frontend/components/Shared/Glow/animations"
import { AnimatedWrapper, Biscuit } from "src/frontend/components/Shared/Glow/Biscuit"

/*
  COMPONENTS
*/

export const PasswordResetSteps: React.FC = () => {
  const { step, token, contact } = usePasswordResetStep()

  if (step === PasswordResetStep.READING_TOKEN) {
    return <ModuleLoading />
  }

  return (
    <AnimatePresence mode="wait">
      <AnimatedWrapper key={step} variants={slide} initial="initial" animate="animate" exit="exit">
        <Biscuit>
          <PasswordResetStepRenderer step={step} token={token} contact={contact} />
        </Biscuit>
      </AnimatedWrapper>
    </AnimatePresence>
  )
}

const PasswordResetStepRenderer: React.FC<{
  step: PasswordResetStep
  token: string | null
  contact?: Contact
}> = ({ step, token, contact }) => {
  switch (step) {
    case PasswordResetStep.RESET_PASSWORD:
      return <ResetPasswordForm token={token || ""} contact={contact} />
    case PasswordResetStep.ENTER_EMAIL:
      return <CreatePasswordReset />
    case PasswordResetStep.EMAIL_SENT:
      return <ResetPasswordSent />
    case PasswordResetStep.EXPIRED:
      return <PasswordResetExpired contact={contact} />
    default:
      return null
  }
}

enum PasswordResetStep {
  READING_TOKEN = "READING_TOKEN",
  RESET_PASSWORD = "RESET_PASSWORD",
  EMAIL_SENT = "EMAIL_SENT",
  ENTER_EMAIL = "ENTER_EMAIL",
  EXPIRED = "EXPIRED",
}

function usePasswordResetStep() {
  const { location, search } = useRouter()
  const token = search.get("t")
  const { contact, loading, isInvalid } = useReadPasswordReset(token)

  return React.useMemo(() => {
    if (token && loading) {
      return { step: PasswordResetStep.READING_TOKEN, token }
    }

    if (isInvalid) {
      return { step: PasswordResetStep.EXPIRED, token: null, contact }
    }

    if (token && contact) {
      return { step: PasswordResetStep.RESET_PASSWORD, token, contact }
    }

    if (location.state?.[PASSWORD_RESET_RECIPIENT]) {
      return { step: PasswordResetStep.EMAIL_SENT, token: null }
    }

    return { step: PasswordResetStep.ENTER_EMAIL, token: null }
  }, [location.state, token, isInvalid, loading, contact])
}
